import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { getCountryByCode } from "../../utils/countryCurrencyData";

interface Order {
  _id: string;
  orderNumber: string;
  user?: {
    _id: string;
    name: string;
    email: string;
  };
  email: string;
  status: "pending" | "confirmed" | "processing" | "shipped" | "delivered" | "cancelled" | "refunded";
  total: number;
  currency: string;
  payment?: {
    _id: string;
    status: string;
    method: string;
  };
  items: Array<{
    product: {
      title: string;
    };
    quantity: number;
    price: number;
  }>;
  shippingAddress?: {
    firstName: string;
    lastName: string;
    address1: string;
    city: string;
  };
  trackingNumber?: string;
  location?: string;
  createdAt: string;
}

interface OrdersListProps {
  orders: Order[];
  loading: boolean;
  page: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  onStatusChange?: (orderId: string, newStatus: string) => void;
  showUser?: boolean;
  showLocation?: boolean;
}

export const OrdersList: React.FC<OrdersListProps> = ({
  orders,
  loading,
  page,
  total,
  limit,
  onPageChange,
  onStatusChange,
  showUser = true,
  showLocation = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "delivered":
        return "bg-green-100 text-green-800";
      case "shipped":
        return "bg-blue-100 text-blue-800";
      case "processing":
        return "bg-yellow-100 text-yellow-800";
      case "pending":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "refunded":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t("admin.orders.noOrders")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("admin.orders.orderNumber")}
              </th>
              {showUser && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("admin.orders.customer")}
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("admin.orders.items")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("admin.orders.total")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("admin.orders.payment")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("admin.orders.status")}
              </th>
              {showLocation && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("admin.orders.location")}
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("admin.orders.date")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("admin.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.map((order) => (
              <tr key={order._id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <button
                    onClick={() => navigate(`/admin/orders/${order._id}`)}
                    className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                  >
                    {order.orderNumber}
                  </button>
                </td>
                {showUser && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {order.user?.name || t("admin.orders.guest")}
                    </div>
                    <div className="text-xs text-gray-500">{order.email}</div>
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {t("admin.orders.itemsCount", { count: order.items.length })}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {order.currency || "$"}{order.total.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {order.payment ? (
                    <div>
                      <span
                        className={`px-2 py-1 text-xs font-semibold ${
                          order.payment.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : order.payment.status === "authorized"
                            ? "bg-blue-100 text-blue-800"
                            : order.payment.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : order.payment.status === "processing"
                            ? "bg-blue-100 text-blue-800"
                            : order.payment.status === "failed"
                            ? "bg-red-100 text-red-800"
                            : order.payment.status === "refunded"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {t(`admin.orders.paymentStatus.${order.payment.status}`) || order.payment.status}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">
                        {t(`admin.orders.paymentMethod.${order.payment.method}`) || order.payment.method}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">{t("admin.orders.noPayment")}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {onStatusChange ? (
                    <select
                      value={order.status}
                      onChange={(e) => onStatusChange(order._id, e.target.value)}
                      className={`px-2 py-1 text-xs font-semibold border border-gray-300 ${getStatusColor(order.status)}`}
                    >
                      <option value="pending">{t("orders.pending")}</option>
                      <option value="confirmed">{t("admin.orders.confirmed")}</option>
                      <option value="processing">{t("orders.processing")}</option>
                      <option value="shipped">{t("orders.shipped")}</option>
                      <option value="delivered">{t("orders.delivered")}</option>
                      <option value="cancelled">{t("orders.cancelled")}</option>
                      <option value="refunded">{t("admin.orders.refunded")}</option>
                    </select>
                  ) : (
                    <span className={`px-2 py-1 text-xs font-semibold ${getStatusColor(order.status)}`}>
                      {t(`orders.${order.status}`) || order.status}
                    </span>
                  )}
                </td>
                {showLocation && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {order.location ? (
                      <span>{getCountryByCode(order.location)?.name || order.location}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(order.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/admin/orders/${order._id}`)}
                  >
                    {t("admin.orders.view")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {t("admin.orders.showing", {
              start: (page - 1) * limit + 1,
              end: Math.min(page * limit, total),
              total,
            })}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page === 1} onClick={() => onPageChange(page - 1)}>
              {t("common.previous")}
            </Button>
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? "primary" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              disabled={page === totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              {t("common.next")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
